/**
 * „Was ist neu“: erscheint einmal nach einem Update als Fenster mit den Neuerungen seit dem letzten Besuch.
 * Neue Spieler ohne Spielstand sehen es nicht (für sie ist alles neu). Alle Versionen stehen auf der Seite /neu (Fußzeile).
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CHANGELOG, LATEST, type Release } from '../../content/changelog';
import { S as liveS } from '../../engine/state';
import { verOf } from '../../engine/version';
import { track } from '../../lib/telemetry';

const SEEN = 'kabinett-neu-gesehen';
const read = (): string | null => { try { return localStorage.getItem(SEEN); } catch { return null; } };
const write = (id: string) => { try { localStorage.setItem(SEEN, id); } catch { /* privater Modus */ } };

/** Läuft eine Einzelspieler-Partie nach älteren Regeln als `rules`? */
function behind(rules?: number): number | null {
  const s: any = liveS;
  if (!rules || !s || s.mp || !s.phase || s.phase === 'program') return null;
  const v = verOf(s);
  return v < rules ? v : null;
}

/** Ein Hinweis für alle gezeigten Releases: Ab welcher Regelversion gilt was? */
function RulesNote({ list }: { list: Release[] }) {
  const max = Math.max(0, ...list.map((r) => r.rules || 0)); if (!max) return null;
  const v = behind(max);
  if (v == null) return <p className="sub wn-rules">Neue Partien starten direkt mit diesen Regeln (Regelversion {max}).</p>;
  return (
    <p className="wn-rules"><b>Für deine laufende Partie:</b> Sie spielt noch nach Regelversion {v}. Die neuen Regeln und die Funktionen, die davon abhängen (markiert mit „ab nächster Legislatur“), kannst du nach der laufenden Legislatur in der Bilanz übernehmen. Bisherige Ergebnisse bleiben erhalten. Neue Partien starten direkt mit Regelversion {max}.</p>
  );
}

export function WhatsNew({ enabled }: { enabled: boolean }) {
  const [auto, setAuto] = useState<Release[] | null>(null);
  const done = useRef(false);
  useEffect(() => {
    if (!enabled || done.current || !LATEST) return;
    done.current = true;
    const seen = read();
    if (seen == null) {
      // Erster Besuch mit dieser Funktion: Wer schon spielt, sieht die neuesten Neuerungen, neue Spieler nicht.
      const s: any = liveS;
      if (s && s.phase && s.phase !== 'program') setAuto(CHANGELOG.slice(0, 2)); else write(LATEST);
      return;
    }
    const fresh = CHANGELOG.filter((r) => r.id > seen);
    if (fresh.length) setAuto(fresh.slice(0, 3));
  }, [enabled]);
  const list = auto;
  if (!list || !list.length) return null;
  const close = () => { write(LATEST); setAuto(null); track('WhatsNew.closed', { id: LATEST }); };
  return (
    <div className="moment-bg" onClick={close}>
      <div className="moment whatsnew" role="dialog" aria-modal="true" aria-label="Was ist neu" onClick={(e) => e.stopPropagation()}>
        <span className="label">Was ist neu</span>
        {list.map((r) => <Rel key={r.id} r={r} />)}
        <RulesNote list={list} />
        <div className="actions"><button type="button" className="yes" onClick={close} autoFocus>Los geht's</button><Link to="/neu" className="linkb" onClick={close}>Alle Neuerungen</Link></div>
      </div>
    </div>
  );
}

/** Eine Version mit ihren Neuerungen. */
function Rel({ r }: { r: Release }) {
  return (
    <section className="wn-rel">
      <h2>{r.title}</h2><span className="sub">{r.date}{r.rules ? ` · Regelversion ${r.rules}` : ''}</span>
      <ul className="wn-list">{r.items.map((it) => (
        <li key={it.t}><b>{it.t}</b>{it.rules && r.rules && behind(r.rules) != null && <span className="tag wn-tag">ab nächster Legislatur</span>}{it.d && <span className="sub">{it.d}</span>}</li>))}</ul>
    </section>
  );
}

/** Seite „Was ist neu“ (/neu): alle Versionen, neueste zuerst. */
export function WhatsNewPage() {
  useEffect(() => { write(LATEST); }, []);
  return (
    <section className="screen whatsnew wn-page">
      <div><span className="label">Was ist neu</span><h2>Neuerungen in Kabinett</h2></div>
      <p className="sub">Alle Updates, neueste zuerst. Ändern sich Spielregeln, laufen bestehende Partien mit ihren bisherigen Regeln weiter. Die neuen Regeln lassen sich nach einer Legislatur in der Bilanz übernehmen.</p>
      <RulesNote list={CHANGELOG} />
      {CHANGELOG.map((r) => <Rel key={r.id} r={r} />)}
      <div className="actions"><Link to="/spiel" className="linkb">Zurück zum Spiel</Link></div>
    </section>
  );
}
